import {
  ANALYTICS_PARTS, ANALYTICS_SECTION_PATHS, AnalyticsAdsPeopleSchema, AnalyticsPersonCardSchema, AnalyticsSalesPeopleSchema, AnalyticsCatalogSchema, AnalyticsPartSchema, ExploreViewSchema, encodeExploreState, AnalyticsLidorubPeopleSchema, AnalyticsMoneyDealsSchema, AnalyticsMoneyPaymentsSchema, AnalyticsConsultationSchema, AnalyticsPracticumPeopleSchema,
  type AnalyticsAdsPeople, type AnalyticsPersonCard, type AnalyticsSheetTab, type AnalyticsSalesPeople, type AnalyticsCatalog, type AnalyticsPart, type AnalyticsSectionKey, type AnalyticsUnit, type ExploreState, type ExploreView, type AnalyticsLidorubPeople, type LidorubMetric, type AnalyticsMoneyDeals, type AnalyticsMoneyPayments, type AnalyticsConsultation, type AnalyticsPracticumPeople,
} from "@vekha/contracts";
import { AnalyticsPeopleSearchSchema, type AnalyticsPeopleSearch } from "@vekha/contracts";
import { AnalyticsSalesCallsSchema, type AnalyticsCallStep, type AnalyticsSalesCalls, AnalyticsVoiceExportSchema, AnalyticsVoiceQuotesSchema, type AnalyticsVoiceDimension, type AnalyticsVoiceExport, type AnalyticsVoiceQuotes } from "@vekha/contracts";
import { AnalyticsMarkResultSchema, type AnalyticsMarkInput, type AnalyticsMarkResult } from "@vekha/contracts";
import { ADS_MAX_PERIOD_DAYS } from "@vekha/contracts";
import { z } from "zod";
import { ApiRequestError } from "./api";

async function read<T>(path: string, parse: (value: unknown) => T, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, { credentials: "include", signal: signal ?? AbortSignal.timeout(120_000), headers: { Accept: "application/json" } });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiRequestError("Аналитика не ответила. Проверьте связь и повторите.", { status: 0 });
  }
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    // A refusal says why when the server does (a shared sheet names the tab it has no right to), else the section's words.
    const said = (payload as { error?: { message?: string } } | null)?.error?.message;
    const message = response.status === 403 ? said ?? "Доступ к этому разделу аналитики не выдан." : response.status === 503 ? "Ядро аналитики сейчас не отвечает. Повторите через минуту."
      : said ?? "Аналитика сейчас недоступна.";
    throw new ApiRequestError(message, { status: response.status });
  }
  return parse(payload);
}

/** The same request of two blocks (or of a re-render) is one request: the answer in flight is shared. */
const inFlight = new Map<string, Promise<unknown>>();
function once<T>(path: string, load: () => Promise<T>): Promise<T> {
  const running = inFlight.get(path);
  if (running) return running as Promise<T>;
  const value = load().finally(() => { if (inFlight.get(path) === value) inFlight.delete(path); });
  inFlight.set(path, value);
  return value;
}

export type PartRequest = { from: string; to: string; compare: boolean; slice?: string | undefined; fresh?: boolean; campaigns?: string | undefined;
  /** «Практикум»: the funnel, the age of the comparison, the launch. */
  funnel?: string | undefined; days?: string | undefined; launch?: string | undefined;
  /** «Практикум» (20.10): "1" — the repeat registrations' road. */
  repeat?: string | undefined;
  /** «Сводка · Что изменилось после»: the days of each side (7 by default). */
  win?: string | undefined };
export function partPath(section: AnalyticsSectionKey, part: string, request: PartRequest): string {
  const params = new URLSearchParams({ from: request.from, to: request.to, compare: request.compare ? "previous" : "none" });
  if (request.slice && request.slice !== "all") params.set("slice", request.slice);
  if (request.campaigns) params.set("campaigns", request.campaigns);
  if (request.funnel) params.set("funnel", request.funnel);
  if (request.days) params.set("days", request.days);
  if (request.launch) params.set("launch", request.launch);
  if (request.repeat) params.set("repeat", request.repeat);
  if (request.win) params.set("win", request.win);
  if (request.fresh) params.set("fresh", "1");
  return `/api/analytics/sections/${section}/${part}?${params}`;
}
/** One part of a section. Not aborted when a page leaves: the answer still lands in the browser's cache for the next visit. */
export function fetchPart(section: AnalyticsSectionKey, part: string, request: PartRequest): Promise<AnalyticsPart> {
  const path = partPath(section, part, request);
  const early = request.fresh ? undefined : prefetched.get(path);
  // Kept for its minute, not taken once: the page may mount twice while the app settles, and the second mount must not
  // ask the same numbers again (17.10: a second wave of every part 0.7 s after the first).
  if (early) return early.catch(() => { if (prefetched.get(path) === early) prefetched.delete(path); return once(path, () => read(path, (value) => AnalyticsPartSchema.parse(value))); });
  return once(path, () => read(path, (value) => AnalyticsPartSchema.parse(value)));
}

/** Parts asked before the page's code came (17.10.2026: «Сводка» waited ~0.6 s for its chunk before asking a number). The
 * page's asks of the same path in that minute take the answer (a failed one is asked anew); then it is dropped. */
const prefetched = new Map<string, Promise<AnalyticsPart>>();
/** «Сводка» asks its own parts; its modules read the sections' parts themselves, in the order the modules stand. */
export const SUMMARY_OWN_PARTS = ["changes", "after"] as const;
export const MODULE_PARTS = [
  ["ads", "roi"], ["ads", "months"], ["practicum", "path"], ["lidorub", "report"], ["lidorub", "team"], ["sales", "managers"], ["sales", "head"],
  ["consultations", "list"], ["money", "period"],
] as const satisfies readonly (readonly [AnalyticsSectionKey, string])[];
/** The parts the page at this address will ask for at once, as it asks them (its default period, the comparison, the days
 * of «до / после»); null — an address whose request the page builds from more than that (a slice, a funnel, a launch). */
export function partsOfAddress(pathname: string, search: string, today = moscowToday()): { section: AnalyticsSectionKey; part: string; request: PartRequest }[] | null {
  const section = (Object.entries(SECTION_PATH) as [AnalyticsSectionKey, string][]).find(([, path]) => path.replace(/\/+$/u, "") === pathname.replace(/\/+$/u, ""))?.[0];
  if (!section) return null;
  const params = new URLSearchParams(search);
  if ([...params.keys()].some((key) => !["from", "to", "cmp", "win"].includes(key))) return null;
  const fallback = presetPeriod(DEFAULT_PRESET[section], today);
  const day = (value: string | null) => /^\d{4}-\d{2}-\d{2}$/u.test(value ?? "") ? value! : null;
  const request: PartRequest = { from: day(params.get("from")) ?? fallback.from, to: day(params.get("to")) ?? fallback.to, compare: params.get("cmp") !== "0",
    ...(section === "summary" && params.get("win") === "14" ? { win: "14" } : {}) };
  if (section !== "summary") return (ANALYTICS_PARTS[section] as readonly string[]).map((part) => ({ section, part, request }));
  return [...SUMMARY_OWN_PARTS.map((part) => ({ section, part: part as string, request })),
    ...MODULE_PARTS.map(([own, part]) => ({ section: own, part: part as string, request: { from: request.from, to: request.to, compare: request.compare } }))];
}
export function prefetchParts(list: { section: AnalyticsSectionKey; part: string; request: PartRequest }[],
  load?: (path: string) => Promise<AnalyticsPart>) {
  const remember = (path: string, answer: Promise<AnalyticsPart>) => {
    answer.catch(() => undefined);
    prefetched.set(path, answer);
    setTimeout(() => { if (prefetched.get(path) === answer) prefetched.delete(path); }, 60_000);
  };
  const fresh = list.filter(({ section, part, request }) => !prefetched.has(partPath(section, part, request)));
  if (load) { for (const { section, part, request } of fresh) { const path = partPath(section, part, request); remember(path, load(path)); } return; }
  // One request for the parts of one period (17.10.2026: eleven side by side held up the login and bootstrap requests).
  const groups = new Map<string, typeof fresh>();
  for (const item of fresh) {
    const query = partPath(item.section, item.part, item.request).split("?")[1]!;
    groups.set(query, [...(groups.get(query) ?? []), item]);
  }
  for (const [query, items] of groups) {
    const batch = read(`/api/analytics/parts?parts=${items.map((item) => `${item.section}.${item.part}`).join(",")}&${query}`,
      (value) => z.object({ parts: z.record(z.string(), z.object({ part: z.unknown().optional(), error: z.string().optional() })) }).parse(value));
    for (const item of items) {
      remember(partPath(item.section, item.part, item.request), batch.then(({ parts }) => {
        const entry = parts[`${item.section}.${item.part}`];
        if (!entry || entry.part === undefined) throw new ApiRequestError(entry?.error ?? "Блок не загрузился.", { status: 0 });
        return AnalyticsPartSchema.parse(entry.part);
      }));
    }
  }
}
export function fetchExplore(state: ExploreState, offset = 0, signal?: AbortSignal): Promise<ExploreView> {
  const params = new URLSearchParams({ x: encodeExploreState(state) });
  if (offset) params.set("offset", String(offset));
  return read(`/api/analytics/explore?${params}`, (value) => ExploreViewSchema.parse(value), signal);
}
/** «Реклама» (16.10): the people behind a number — a row (total, a cabinet, a campaign or the launches of one), its step, a group. */
export type AdsPeopleRequest = { from: string; to: string; slice?: string | undefined; row?: string | undefined; campaigns?: string[] | undefined;
  step?: string | undefined; group?: string | undefined; offset?: number } & SheetFindRequest;
const peopleSeen = new Map<string, Promise<AnalyticsAdsPeople | AnalyticsPersonCard | AnalyticsSalesPeople | AnalyticsConsultation>>();
/** A sheet opened again (back, a link) is drawn at once: the answers of this visit are kept, the last 30. */
export function fetchAdsPeople(request: AdsPeopleRequest): Promise<AnalyticsAdsPeople> {
  const params = new URLSearchParams({ from: request.from, to: request.to });
  if (request.slice && request.slice !== "all") params.set("slice", request.slice);
  if (request.row) params.set("row", request.row);
  if (request.campaigns?.length) params.set("campaigns", request.campaigns.join(","));
  if (request.step) params.set("step", request.step);
  if (request.group) params.set("group", request.group);
  if (request.offset) params.set("offset", String(request.offset));
  findParams(params, request);
  const path = `/api/analytics/sections/ads/people?${params}`;
  const known = peopleSeen.get(path) as Promise<AnalyticsAdsPeople> | undefined;
  if (known) return known;
  const value = read(path, (payload) => AnalyticsAdsPeopleSchema.parse(payload));
  value.catch(() => peopleSeen.delete(path));
  peopleSeen.set(path, value);
  if (peopleSeen.size > 30) peopleSeen.delete(peopleSeen.keys().next().value!);
  return value;
}

/** «Продажи» (16.10): the people behind a number — a step for the team or one manager, a group of its people. */
export type SalesPeopleRequest = { from: string; to: string; step: string; manager?: string | undefined; group?: string | undefined; offset?: number;
  /** 17.10 (bits): the key of a row of «Страны». */
  country?: string | undefined;
  /** 17.10 (path): «Через что пришли» — a row, a mark, a way; `trail` — every person with their way. */
  who?: string | undefined; webinar?: string | undefined; practicum?: string | undefined; path?: string | undefined; trail?: boolean | undefined } & SheetFindRequest;
export function fetchSalesPeople(request: SalesPeopleRequest): Promise<AnalyticsSalesPeople> {
  const params = new URLSearchParams({ from: request.from, to: request.to, step: request.step });
  if (request.manager) params.set("manager", request.manager);
  if (request.country) params.set("country", request.country);
  for (const key of ["who", "webinar", "practicum", "path"] as const) if (request[key]) params.set(key, request[key]!);
  if (request.trail) params.set("trail", "1");
  if (request.group) params.set("group", request.group);
  if (request.offset) params.set("offset", String(request.offset));
  findParams(params, request);
  const path = `/api/analytics/sections/sales/people?${params}`;
  const known = peopleSeen.get(path) as Promise<AnalyticsSalesPeople> | undefined;
  if (known) return known;
  const value = read(path, (payload) => AnalyticsSalesPeopleSchema.parse(payload));
  value.catch(() => peopleSeen.delete(path));
  peopleSeen.set(path, value);
  if (peopleSeen.size > 30) peopleSeen.delete(peopleSeen.keys().next().value!);
  return value;
}
/** «Практикум» (16.10): the people behind a number — reached a step, stopped before it, came to sales by a road, met a failure. */
export type PracticumPeopleRequest = { from: string; to: string; funnel?: string | undefined; repeat?: string | undefined; days?: string | undefined; step?: string | undefined; lost?: string | undefined;
  road?: string | undefined; after?: string | undefined; way?: string | undefined; failure?: string | undefined; offset?: number;
  code?: string | undefined; failureOn?: string | undefined; leftAt?: string | undefined; pressedFrom?: string | undefined; pressedTo?: string | undefined;
  bookedBy?: string | undefined; origin?: string | undefined; resumed?: string | undefined; circleStep?: string | undefined;
  lesson?: string | undefined; watch?: string | undefined; click?: string | undefined; circleVersion?: string | undefined; versionStep?: string | undefined;
  mark?: string | undefined } & SheetFindRequest;
export function fetchPracticumPeople(request: PracticumPeopleRequest): Promise<AnalyticsPracticumPeople> {
  const params = new URLSearchParams({ from: request.from, to: request.to });
  for (const key of ["funnel", "repeat", "days", "step", "lost", "road", "after", "way", "failure", "code", "failureOn", "leftAt", "pressedFrom", "pressedTo", "bookedBy", "origin", "resumed", "circleStep",
    "lesson", "watch", "click", "circleVersion", "versionStep", "mark"] as const) { const value = request[key]; if (value) params.set(key, value); }
  if (request.offset) params.set("offset", String(request.offset));
  findParams(params, request);
  const path = `/api/analytics/sections/practicum/people?${params}`;
  const known = peopleSeen.get(path) as Promise<AnalyticsPracticumPeople> | undefined;
  if (known) return known;
  const value = read(path, (payload) => AnalyticsPracticumPeopleSchema.parse(payload));
  value.catch(() => peopleSeen.delete(path));
  peopleSeen.set(path, value as Promise<never>);
  if (peopleSeen.size > 30) peopleSeen.delete(peopleSeen.keys().next().value!);
  return value;
}

/** The full card of a person (the shared sheet), opened from `tab` — the API opens it by that tab's permission (16.10).
 * Kept like the sheets, for going back and forth. */
export function fetchPersonCard(person: string, tab: AnalyticsSheetTab): Promise<AnalyticsPersonCard> {
  const path = `/api/analytics/person?${new URLSearchParams({ person, tab })}`;
  const known = peopleSeen.get(path) as Promise<AnalyticsPersonCard> | undefined;
  if (known) return known;
  const value = read(path, (payload) => AnalyticsPersonCardSchema.parse(payload));
  value.catch(() => peopleSeen.delete(path));
  peopleSeen.set(path, value);
  if (peopleSeen.size > 30) peopleSeen.delete(peopleSeen.keys().next().value!);
  return value;
}
/** «Лидорубы» (16.10): the people behind a number of either lidorub — a metric, maybe one day or one type of person. */
export type LidorubPeopleRequest = { from: string; to: string; metric: LidorubMetric; day?: string | undefined; type?: string | undefined; source?: string | undefined; offset?: number  } & SheetFindRequest;
export function fetchLidorubPeople(request: LidorubPeopleRequest): Promise<AnalyticsLidorubPeople> {
  const params = new URLSearchParams({ from: request.from, to: request.to, metric: request.metric });
  if (request.day) params.set("day", request.day);
  if (request.type) params.set("type", request.type);
  if (request.source) params.set("source", request.source);
  if (request.offset) params.set("offset", String(request.offset));
  findParams(params, request);
  const path = `/api/analytics/sections/lidorub/people?${params}`;
  const known = lidorubSeen.get(path);
  if (known) return known;
  const value = read(path, (payload) => AnalyticsLidorubPeopleSchema.parse(payload));
  value.catch(() => lidorubSeen.delete(path));
  lidorubSeen.set(path, value);
  if (lidorubSeen.size > 30) lidorubSeen.delete(lidorubSeen.keys().next().value!);
  return value;
}
const lidorubSeen = new Map<string, Promise<AnalyticsLidorubPeople>>();
/** «Деньги» (16.10): the payments behind a number and the deals behind a month of the receivables. Kept like the sheets of «Реклама». */
export type MoneyPaymentsRequest = { from: string; to: string; kind?: string | undefined; arrival?: string | undefined; manager?: string | undefined; mark?: string | undefined;
  country?: string | undefined; campaign?: string | undefined; category?: string | undefined; day?: string | undefined; offset?: number } & SheetFindRequest;
const moneySeen = new Map<string, Promise<AnalyticsMoneyPayments | AnalyticsMoneyDeals>>();
function keptMoney<T extends AnalyticsMoneyPayments | AnalyticsMoneyDeals>(path: string, parse: (value: unknown) => T): Promise<T> {
  const known = moneySeen.get(path) as Promise<T> | undefined;
  if (known) return known;
  const value = read(path, parse);
  value.catch(() => moneySeen.delete(path));
  moneySeen.set(path, value);
  if (moneySeen.size > 30) moneySeen.delete(moneySeen.keys().next().value!);
  return value;
}
export function fetchMoneyPayments(request: MoneyPaymentsRequest): Promise<AnalyticsMoneyPayments> {
  const params = new URLSearchParams({ from: request.from, to: request.to });
  for (const key of ["kind", "arrival", "manager", "mark", "country", "campaign", "category", "day"] as const) if (request[key] !== undefined) params.set(key, request[key]!);
  if (request.offset) params.set("offset", String(request.offset));
  findParams(params, request);
  return keptMoney(`/api/analytics/sections/money/payments?${params}`, (payload) => AnalyticsMoneyPaymentsSchema.parse(payload));
}
export function fetchMoneyDeals(request: { month: string; manager?: string | undefined; offset?: number } & SheetFindRequest): Promise<AnalyticsMoneyDeals> {
  const params = new URLSearchParams({ month: request.month });
  if (request.manager !== undefined) params.set("manager", request.manager);
  if (request.offset) params.set("offset", String(request.offset));
  findParams(params, request);
  return keptMoney(`/api/analytics/sections/money/deals?${params}`, (payload) => AnalyticsMoneyDealsSchema.parse(payload));
}
/** «Консультации»: one consultation (the shared sheet of the layer), by its id `<deal>:<day>`, opened from `tab` (its permission
 * opens it, as the card). Kept like the sheets. */
export function fetchConsultation(id: string, tab: AnalyticsSheetTab): Promise<AnalyticsConsultation> {
  const path = `/api/analytics/consultation?${new URLSearchParams({ consultation: id, tab })}`;
  const known = peopleSeen.get(path) as Promise<AnalyticsConsultation> | undefined;
  if (known) return known;
  const value = read(path, (payload) => AnalyticsConsultationSchema.parse(payload));
  value.catch(() => peopleSeen.delete(path));
  peopleSeen.set(path, value);
  if (peopleSeen.size > 30) peopleSeen.delete(peopleSeen.keys().next().value!);
  return value;
}
export function fetchAnalyticsCatalog(): Promise<AnalyticsCatalog> {
  return once("/api/analytics/catalog", () => read("/api/analytics/catalog", (value) => AnalyticsCatalogSchema.parse(value)));
}

// ————— The browser's copy of the last answers: a section opens at once, then refreshes —————

const CACHE_PREFIX = "vekha.analytics.v2|";
const CACHE_KEEP = 40;
const CACHE_DAYS = 7;
/** The last answer of this part for these settings, as this browser saw it; null when there is none (or storage is off). */
export function cachedPart(section: AnalyticsSectionKey, part: string, request: PartRequest): AnalyticsPart | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + partPath(section, part, { ...request, fresh: false }));
    if (!raw) return null;
    const saved = JSON.parse(raw) as { at: number; value: unknown };
    if (Date.now() - saved.at > CACHE_DAYS * 86_400_000) return null;
    const parsed = AnalyticsPartSchema.safeParse(saved.value);
    return parsed.success ? parsed.data : null;
  } catch { return null; }
}
export function rememberPart(section: AnalyticsSectionKey, part: string, request: PartRequest, value: AnalyticsPart): void {
  try {
    localStorage.setItem(CACHE_PREFIX + partPath(section, part, { ...request, fresh: false }), JSON.stringify({ at: Date.now(), value }));
    const keys = Object.keys(localStorage).filter((key) => key.startsWith(CACHE_PREFIX));
    if (keys.length > CACHE_KEEP) {
      const dated = keys.map((key) => { try { return { key, at: (JSON.parse(localStorage.getItem(key) ?? "{}") as { at?: number }).at ?? 0 }; } catch { return { key, at: 0 }; } });
      for (const { key } of dated.sort((a, b) => a.at - b.at).slice(0, keys.length - CACHE_KEEP)) localStorage.removeItem(key);
    }
  } catch { /* A full or closed storage only costs the instant start of the next visit. */ }
}

// ————— Periods —————

export const DAY_MS = 86_400_000;
export function moscowToday(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Moscow", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}
export const addDays = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
const weekday = (day: string) => (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
/** «Всё время» — only «Реклама»: from the first day of the Google Ads export (the core says it; 06.06.2026 until it has).
 * «Вчера» and «Прошлый месяц» (Ден 17.10): every section; «Реклама» has its own buttons (AdsPeriod: «Вчера», the months). */
export const PERIOD_PRESETS = [
  { key: "all", label: "Всё время", only: "ads" }, { key: "yesterday", label: "Вчера" }, { key: "7d", label: "7 дней" }, { key: "week", label: "Эта неделя" },
  { key: "last_week", label: "Прошлая неделя" }, { key: "month", label: "Месяц" }, { key: "last_month", label: "Прошлый месяц" }, { key: "30d", label: "30 дней" },
  { key: "90d", label: "3 месяца", only: ["consultations", "voice"] },
] as const;
const ADS_EXPORT_KEY = "vekha.analytics.adsExportFrom";
// The export's first day as the core answers it (exportFrom, 17.10.2026): a browser that has not seen «Реклама» yet asked
// «Всё время» from 06.06, learned 01.03 from the answer and asked the page again when it next rendered — the numbers
// changed 3–5 s after they came.
const ADS_EXPORT_FALLBACK = "2026-03-01";
export function adsExportFrom(): string {
  try { const saved = localStorage.getItem(ADS_EXPORT_KEY); return saved && /^\d{4}-\d{2}-\d{2}$/u.test(saved) ? saved : ADS_EXPORT_FALLBACK; } catch { return ADS_EXPORT_FALLBACK; }
}
export function rememberAdsExportFrom(day: string): void {
  try { if (/^\d{4}-\d{2}-\d{2}$/u.test(day)) localStorage.setItem(ADS_EXPORT_KEY, day); } catch { /* the fallback day stays */ }
}
export const presetsOf = (section: AnalyticsSectionKey) => PERIOD_PRESETS.filter((preset) => !("only" in preset) || ([preset.only].flat() as string[]).includes(section));
/** «Реклама» (16.10): its periods are the months of the export (the last five), the whole export and any days. A month
 * that is still going ends yesterday: the core builds its data at night. */
export function adsMonths(today: string, exportFrom = adsExportFrom(), count = 5): { month: string; label: string; from: string; to: string }[] {
  const months: string[] = [];
  for (let month = today.slice(0, 7); month >= exportFrom.slice(0, 7) && months.length < count; month = addDays(`${month}-01`, -1).slice(0, 7)) months.unshift(month);
  return months.map((month) => {
    const first = `${month}-01`, last = addDays(addDays(`${month}-28`, 4).slice(0, 7) + "-01", -1);
    const from = first < exportFrom ? exportFrom : first, end = last < today ? last : addDays(today, -1);
    return { month, label: monthLabel(month), from: end < from ? today : from, to: end < from ? today : end };
  });
}
export type PeriodPreset = (typeof PERIOD_PRESETS)[number]["key"];
/** «7 дней» and «30 дней» are whole days, up to yesterday: the core builds its data at night, today is not over. */
export function presetPeriod(preset: PeriodPreset, today: string): { from: string; to: string } {
  switch (preset) {
    // The day before today, whole: the core builds its data at night.
    case "yesterday": return { from: addDays(today, -1), to: addDays(today, -1) };
    case "7d": return { from: addDays(today, -7), to: addDays(today, -1) };
    case "week": return { from: addDays(today, -weekday(today)), to: today };
    case "last_week": { const monday = addDays(today, -weekday(today) - 7); return { from: monday, to: addDays(monday, 6) }; }
    case "month": return { from: `${today.slice(0, 7)}-01`, to: today };
    // The whole calendar month before this one.
    case "last_month": { const last = addDays(`${today.slice(0, 7)}-01`, -1); return { from: `${last.slice(0, 7)}-01`, to: last }; }
    case "30d": return { from: addDays(today, -30), to: addDays(today, -1) };
    // «Консультации», «Голос клиента»: the Lab says more of three months of consultations than of one.
    case "90d": return { from: addDays(today, -90), to: addDays(today, -1) };
    // The whole export, at most the days a period of «Реклама» may have (ADS_MAX_PERIOD_DAYS).
    case "all": { const from = adsExportFrom(), limit = addDays(today, -(ADS_MAX_PERIOD_DAYS - 1)); return { from: from < limit ? limit : from, to: addDays(today, -1) }; }
  }
}
/** The button behind the days. `chosen` — the one the reader pressed (the address's `p`): on a Monday «7 дней» and «Прошлая
 * неделя» are the same days, and the first of them by its dates lit «7 дней» for «Прошлая неделя» (Паша 20.10). Without it — by the dates. */
export function presetOf(from: string, to: string, today: string, chosen?: string | null): PeriodPreset | null {
  const pressed = PERIOD_PRESETS.find((preset) => preset.key === chosen)?.key;
  if (pressed) { const period = presetPeriod(pressed, today); if (period.from === from && period.to === to) return pressed; }
  return PERIOD_PRESETS.find((preset) => { const period = presetPeriod(preset.key, today); return period.from === from && period.to === to; })?.key ?? null;
}
/** The morning's summary and sales — a week; ads and ways in — 30 days (buyers need time); Юля and money — the month, as her tab and the plan. */
/** «Реклама» opens on the whole export: a campaign's payback is over its life (Vlad's table, 14.10); fresh traffic has its own column. */
export const DEFAULT_PRESET: Record<AnalyticsSectionKey, PeriodPreset> = { summary: "month", ads: "all", practicum: "30d", entries: "30d", sales: "7d", money: "month", lidorub: "month",
  consultations: "30d", voice: "90d" };
export const SECTION_PATH = ANALYTICS_SECTION_PATHS;
/** The same days a month before («Деньги», 17.10, Ден «к прошлому месяцу»), as the API's monthAgoOf: a day past the end of
 * the shorter month is its last day, and the last day of a month stays the last. */
export function monthAgo(period: { from: string; to: string }): { from: string; to: string } {
  const shift = (day: string) => {
    const [y, m, d] = parts(day);
    const lengthOf = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();
    const year = m === 1 ? y - 1 : y, month = m === 1 ? 12 : m - 1, length = lengthOf(year, month);
    const pick = d === lengthOf(y, m) ? length : Math.min(d, length);
    return `${year}-${String(month).padStart(2, "0")}-${String(pick).padStart(2, "0")}`;
  };
  return { from: shift(period.from), to: shift(period.to) };
}
/** «с прошлой неделей (29 сен – 5 окт)»: the comparison names the kind of period, not only its dates. «Деньги» compare with
 * the same days a month before (bits 17.10: the switch said «с прошлыми N днями» there). */
export function compareLabel(from: string, to: string, today: string, section?: AnalyticsSectionKey, chosen?: string | null): string {
  const preset = presetOf(from, to, today, chosen);
  if (section === "money") {
    const before = monthAgo({ from, to });
    return `${preset === "last_month" ? "с позапрошлым месяцем" : "с теми же днями месяц назад"} (${periodLabel(before.from, before.to, true)})`;
  }
  const length = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
  const previous = periodLabel(addDays(from, -length), addDays(from, -1), true);
  // «Вчера» against the day before it (the comparison of Vekha: as many days just before), «позавчера».
  const kind = preset === "last_week" ? "с позапрошлой неделей" : preset === "week" ? "с теми же днями прошлой недели" : preset === "yesterday" ? "с позавчера"
    : `с прошлыми ${length} ${pluralRu(length, "днём", "днями", "днями")}`;
  return `${kind} (${previous})`;
}

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const MONTHS_NOMINATIVE = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
const parts = (day: string) => day.split("-").map(Number) as [number, number, number];
/** «6–12 октября», «28 сентября – 4 октября»; with the year when the period crosses one or is not this year (20.10.2026:
 * «1 марта 2026 – 19 марта 2027» was «1–19 марта» — the same month by its number alone, thirteen months read as one). */
export function periodLabel(from: string, to: string, short = false, today = moscowToday()): string {
  const names = short ? MONTHS_SHORT : MONTHS;
  const [fy, fm, fd] = parts(from), [ty, tm, td] = parts(to);
  if (fy !== ty) return `${fd} ${names[fm - 1]} ${fy} – ${td} ${names[tm - 1]} ${ty}`;
  const year = ty === Number(today.slice(0, 4)) ? "" : ` ${ty}`;
  if (from === to) return `${td} ${names[tm - 1]}${year}`;
  return fm === tm ? `${fd}–${td} ${names[tm - 1]}${year}` : `${fd} ${names[fm - 1]} – ${td} ${names[tm - 1]}${year}`;
}
export const dayLabel = (day: string) => { const [, m, d] = parts(day); return `${d} ${MONTHS_SHORT[m - 1]}`; };
export const monthLabel = (month: string) => MONTHS_NOMINATIVE[Number(month.slice(5, 7)) - 1] ?? month;
export const WEEKDAYS = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];
export const weekdayOf = (day: string) => WEEKDAYS[weekday(day)]!;

// ————— Numbers —————

const integer = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
export function formatEur(value: number | null): string {
  if (value === null) return "—";
  return `${integer.format(Math.round(value))} €`;
}
/** 12,3 тыс € — where space is short (axes, chart labels). */
export function formatEurShort(value: number | null): string {
  if (value === null) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(value / 1_000_000)} млн €`;
  if (abs >= 10_000) return `${integer.format(Math.round(value / 1000))} тыс €`;
  if (abs >= 1000) return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(value / 1000)} тыс €`;
  return `${integer.format(Math.round(value))} €`;
}
export const formatCount = (value: number | null) => value === null ? "—" : integer.format(value);
export function formatShare(value: number | null, digits = 0): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const percent = value * 100;
  if (percent > 0 && percent < 1 && digits === 0) return "<1%";
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: digits }).format(percent)}%`;
}
export function formatValue(value: number | null, unit: AnalyticsUnit): string {
  return unit === "eur" ? formatEur(value) : unit === "ratio" ? formatShare(value) : formatCount(value);
}
export function formatValueShort(value: number | null, unit: AnalyticsUnit): string {
  return unit === "eur" ? formatEurShort(value) : formatValue(value, unit);
}
export type Delta = { text: string; tone: "good" | "bad" | "flat" | "neutral"; direction: "up" | "down" | "flat" };
/** Percentage points in words (Ден 18.10: «п.п.» is not read): «2 пункта», «5 пунктов», «11,6 пункта». */
export function pointsWord(value: number): string {
  const abs = Math.abs(value);
  return Number.isInteger(abs) ? pluralRu(abs, "пункт", "пункта", "пунктов") : "пункта";
}
/** Against the previous period: % for amounts, points for shares; colour by meaning (growth of spend is neither). */
export function deltaOf(value: number | null, previous: number | null, unit: AnalyticsUnit, good: "up" | "down" | "none"): Delta | null {
  if (value === null || previous === null) return null;
  const diff = value - previous;
  // A change that rounds to nothing is «как было», no arrow (видео 18.10, Н3: «Красная зона 10 % ↘ −0 пунктов»).
  if (unchanged(value, previous, unit)) return { text: SAME, tone: "flat", direction: "flat" };
  const direction = Math.abs(diff) < 1e-9 ? "flat" : diff > 0 ? "up" : "down";
  let text: string;
  if (unit === "ratio") text = pointsText(diff * 100);
  // «Было 0» is growth from nothing, not «новое»: that word is kept for what appeared for the first time.
  else if (previous === 0) text = `+${unit === "eur" ? formatEurShort(value) : integer.format(value)} · раньше 0`;
  else { const pct = Math.round((diff / Math.abs(previous)) * 100); text = `${pct > 0 ? "+" : "−"}${Math.abs(pct)}%`; }
  const small = unit === "ratio" ? Math.abs(diff) < 0.02 : previous !== 0 && Math.abs(diff / previous) < 0.03;
  const tone = direction === "flat" || small ? "flat" : good === "none" ? "neutral" : (direction === good) ? "good" : "bad";
  return { text, tone, direction };
}
/** «как было»: what a change says when it rounds to nothing in its own unit. */
export const SAME = "как было";
/** Whether a change shows as nothing: under half a point for shares, under half a percent for amounts (from 0 — only 0). */
export function unchanged(value: number, previous: number, unit: AnalyticsUnit): boolean {
  if (unit === "ratio") return Math.round(Math.abs(value - previous) * 100) === 0;
  return previous === 0 ? value === 0 : Math.round(Math.abs((value - previous) / previous) * 100) === 0;
}
/** Points of a change in words, «как было» under half a point (the gap of two shares, a step before and after). */
export function pointsText(points: number, digits = 0): string {
  const shown = Math.round(Math.abs(points) * 10 ** digits) / 10 ** digits;
  if (shown === 0) return SAME;
  return `${points > 0 ? "+" : "−"}${shown.toLocaleString("ru-RU", { maximumFractionDigits: digits })} ${pointsWord(shown)}`;
}
export function pluralRu(count: number, one: string, few: string, many: string): string {
  const m100 = Math.abs(count) % 100, m10 = m100 % 10;
  return m100 >= 11 && m100 <= 19 ? many : m10 === 1 ? one : m10 >= 2 && m10 <= 4 ? few : many;
}
export const peopleRu = (count: number) => `${formatCount(count)} ${pluralRu(count, "человек", "человека", "человек")}`;

/** The badge (17.10): the release's time and, beside it, each source the core read over an hour earlier, in plain words —
 * «данные на 19:39 · расход Google Ads на 14:40». `metas` — the page's parts; a source keeps its oldest time. */
export function releaseFreshness(metas: { releaseAsOf?: string | null; dataAsOf: string | null; lags?: { label: string; at: string }[] }[], now = Date.now()):
  { label: string; base: string; tone: "fresh" | "stale" | "missing"; lags: string[] } {
  const release = metas.map((m) => m.releaseAsOf ?? m.dataAsOf).filter((v): v is string => !!v).sort().at(-1) ?? null;
  const base = freshnessOf(release, now);
  const lags = new Map<string, string>();
  for (const meta of metas) for (const lag of meta.lags ?? []) { const known = lags.get(lag.label); if (!known || lag.at < known) lags.set(lag.label, lag.at); }
  const clock = (iso: string) => {
    const at = new Date(iso);
    const time = new Intl.DateTimeFormat("ru-RU", { timeZone: "Europe/Moscow", hour: "2-digit", minute: "2-digit" }).format(at);
    return moscowToday(at) === moscowToday(new Date(now)) ? time : `${dayLabel(moscowToday(at))} ${time}`;
  };
  const words = release ? [...lags].filter(([, at]) => Date.parse(release) - Date.parse(at) > 3_600_000).sort((a, b) => a[1].localeCompare(b[1]))
    .map(([label, at]) => `${label} на ${clock(at)}`) : [];
  return { label: [base.label, ...words].join(" · "), base: base.label, tone: base.tone, lags: words };
}

export function freshnessOf(dataAsOf: string | null, now = Date.now()): { label: string; tone: "fresh" | "stale" | "missing" } {
  if (!dataAsOf) return { label: "время данных неизвестно", tone: "missing" };
  const at = new Date(dataAsOf);
  const time = new Intl.DateTimeFormat("ru-RU", { timeZone: "Europe/Moscow", hour: "2-digit", minute: "2-digit" }).format(at);
  const day = moscowToday(at) === moscowToday(new Date(now)) ? "" : ` ${dayLabel(moscowToday(at))}`;
  return { label: `данные на ${time}${day}`, tone: now - at.getTime() > 12 * 3_600_000 ? "stale" : "fresh" };
}

// ————— Find a person (17.10, find): the field over every section, a sheet's search and «Скачать список» —————

/** A sheet's search (`find` — the core's search narrows its list) and the whole list at once (`all` — for the download). */
export type SheetFindRequest = { find?: string | undefined; all?: boolean | undefined };
function findParams(params: URLSearchParams, request: SheetFindRequest): void {
  if (request.find) params.set("find", request.find);
  if (request.all) params.set("all", "1");
}
/** The search of a person, asked from a tab (its permission opens it, as the card it leads to); a newer query aborts the older. */
export function fetchPeopleSearch(q: string, tab: AnalyticsSheetTab, signal?: AbortSignal): Promise<AnalyticsPeopleSearch> {
  return read(`/api/analytics/search?${new URLSearchParams({ q, tab })}`, (payload) => AnalyticsPeopleSearchSchema.parse(payload), signal);
}
/** «Продажи» 17.10 (ответ 9.4): the calls behind a number of «Звонки менеджеров» — a step, maybe one manager or one day. */
export type SalesCallsRequest = { from: string; to: string; step: AnalyticsCallStep; manager?: string | undefined; day?: string | undefined; offset?: number };
const callsSeen = new Map<string, Promise<AnalyticsSalesCalls>>();
export function fetchSalesCalls(request: SalesCallsRequest): Promise<AnalyticsSalesCalls> {
  const params = new URLSearchParams({ from: request.from, to: request.to, step: request.step });
  if (request.manager) params.set("manager", request.manager);
  if (request.day) params.set("day", request.day);
  if (request.offset) params.set("offset", String(request.offset));
  const path = `/api/analytics/sections/sales/call-list?${params}`;
  const known = callsSeen.get(path);
  if (known) return known;
  const value = read(path, (payload) => AnalyticsSalesCallsSchema.parse(payload));
  value.catch(() => callsSeen.delete(path));
  callsSeen.set(path, value);
  if (callsSeen.size > 30) callsSeen.delete(callsSeen.keys().next().value!);
  return value;
}

// ————— «Голос клиента» (17.10, вечер): the quotes of a theme and the text for a neural network —————

/** The page's filters, the same for a theme's sheet and the export. */
export type VoiceFilterRequest = { bought?: "yes" | "no" | undefined; source?: string | undefined; segment?: string | undefined };
const voiceParams = (request: { from: string; to: string } & VoiceFilterRequest) => {
  const params = new URLSearchParams({ from: request.from, to: request.to });
  for (const key of ["bought", "source", "segment"] as const) if (request[key] !== undefined) params.set(key, request[key]!);
  return params;
};
const voiceSeen = new Map<string, Promise<unknown>>();
function keptVoice<T>(path: string, parse: (payload: unknown) => T): Promise<T> {
  const known = voiceSeen.get(path) as Promise<T> | undefined;
  if (known) return known;
  const value = read(path, parse);
  value.catch(() => voiceSeen.delete(path));
  voiceSeen.set(path, value);
  if (voiceSeen.size > 30) voiceSeen.delete(voiceSeen.keys().next().value!);
  return value;
}
export function fetchVoiceQuotes(request: { from: string; to: string; dimension: AnalyticsVoiceDimension; theme: string } & VoiceFilterRequest): Promise<AnalyticsVoiceQuotes> {
  const params = voiceParams(request);
  params.set("dimension", request.dimension);
  params.set("theme", request.theme);
  return keptVoice(`/api/analytics/sections/voice/quotes?${params}`, (payload) => AnalyticsVoiceQuotesSchema.parse(payload));
}
export function fetchVoiceExport(request: { from: string; to: string } & VoiceFilterRequest): Promise<AnalyticsVoiceExport> {
  return keptVoice(`/api/analytics/sections/voice/export?${voiceParams(request)}`, (payload) => AnalyticsVoiceExportSchema.parse(payload));
}

// ————— Marks of changes (20.10, Ден): «Добавить изменение» and «Убрать» on «Сводка» —————

async function markMutation(url: string, body: unknown, csrfToken: string | null): Promise<AnalyticsMarkResult> {
  if (!csrfToken) throw new ApiRequestError("Защитный токен сессии недоступен. Обновите страницу и повторите.", { code: "CSRF_TOKEN_UNAVAILABLE" });
  let response: Response;
  try {
    response = await fetch(url, { method: "POST", credentials: "include", signal: AbortSignal.timeout(20_000),
      headers: { Accept: "application/json", "Content-Type": "application/json", "x-csrf-token": csrfToken }, body: JSON.stringify(body) });
  } catch {
    throw new ApiRequestError("Сервер не ответил. Обновите страницу и проверьте, сохранилось ли.");
  }
  let payload: unknown = null;
  try { payload = await response.json(); } catch { payload = null; }
  if (!response.ok) {
    const code = (payload as { error?: { code?: string } } | null)?.error?.code;
    const message = response.status === 403 ? "Отметки добавляет и убирает владелец или администратор."
      : response.status === 400 ? "Проверьте поля: дата, раздел и что поменялось (до 80 знаков)." : "Не получилось сохранить. Повторите через минуту.";
    throw new ApiRequestError(message, { status: response.status, ...(code ? { code } : {}) });
  }
  return AnalyticsMarkResultSchema.parse(payload);
}

/** A change outside the code: the core gathers it within two minutes and draws it on every chart by day. */
export function addChangeMark(input: AnalyticsMarkInput, csrfToken: string | null): Promise<AnalyticsMarkResult> {
  return markMutation("/api/analytics/sections/summary/marks", input, csrfToken);
}

/** A wrong mark leaves every chart (`hidden: false` — brings it back). */
export function hideChangeMark(id: string, hidden: boolean, csrfToken: string | null): Promise<AnalyticsMarkResult> {
  return markMutation(`/api/analytics/sections/summary/marks/${hidden ? "hide" : "unhide"}`, { id }, csrfToken);
}
